import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../services/supabaseClient';
import postgresBackendService from '../services/postgresBackendService';
import config from '../config';
import i18n from '../i18n';
import { traceRouting } from '../utils/routingDebug';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // Perfil de PostgreSQL
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  // Función para cargar el perfil del usuario desde PostgreSQL.
  // Returns the loaded profile (or null on error/404) so callers can use it
  // synchronously without waiting for state propagation.
  const loadUserProfile = async (token) => {
    if (!token) {
      console.warn('[AuthContext] No token available to load profile');
      return null;
    }

    const startedAt = Date.now();
    try {
      setProfileLoading(true);
      console.log('[AuthContext] Loading user profile from PostgreSQL...');

      const response = await fetch(`${config.apiUrl}/api/users/me/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const elapsedMs = Date.now() - startedAt;

      if (response.ok) {
        const json = await response.json();
        // Unwrap standardized response format: { success, data }
        const profile = json.data !== undefined ? json.data : json;
        console.log('[AuthContext] User profile loaded:', {
          email: profile?.email?.substring(0, 3) + '***',
          hasName: !!profile?.associated_person,
          elapsedMs
        });
        setUserProfile(profile);
        // Sync UI language from DB preference (overrides localStorage on login)
        if (profile?.language && profile.language !== i18n.resolvedLanguage) {
          i18n.changeLanguage(profile.language);
        }
        return profile;
      } else if (response.status === 404) {
        // 404 here on a freshly-confirmed user means ensureUserProfileSync failed
        // to create the row (e.g. SUPABASE_SERVICE_KEY misconfigured, admin API
        // failure, or transient auth.users propagation delay). Surface the
        // backend body so we can tell which case we're in.
        let body = null;
        try { body = await response.text(); } catch { /* ignore */ }
        console.warn('[AuthContext] User profile NOT FOUND (404) — likely ensureUserProfileSync failed', {
          elapsedMs,
          body
        });
        setUserProfile(null);
        return null;
      } else {
        let body = null;
        try { body = await response.text(); } catch { /* ignore */ }
        console.error('[AuthContext] Error loading user profile:', { status: response.status, elapsedMs, body });
        setUserProfile(null);
        return null;
      }
    } catch (error) {
      const elapsedMs = Date.now() - startedAt;
      console.error('[AuthContext] Error fetching user profile:', { elapsedMs, error: error?.message || error });
      setUserProfile(null);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Diagnostic: surface PKCE code presence on mount so we can tell whether
    // the user arrived from a Supabase email confirmation link.
    const urlHasCode = new URLSearchParams(window.location.search).has('code');
    console.log('[AuthContext] useEffect mounted', { urlHasCode, path: window.location.pathname });

    setLoading(true);

    // 1. Obtener la sesión inicial
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (isMounted) {
        setSession(session);
        setUser(session?.user ?? null);

        // Logs mejorados para debugging
        console.log('[AuthContext] Session loaded:', {
          user: session?.user ? {
            id: session.user.id?.substring(0, 6) + '...',
            email: session.user.email?.substring(0, 3) + '***@' + session.user.email?.split('@')[1]
          } : null,
          expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
          expiresIn: session?.expires_at
            ? `${Math.round((session.expires_at * 1000 - Date.now()) / 1000 / 60)} min`
            : null
        });

        // 2. Si hay sesión, cargar perfil de PostgreSQL ANTES de setLoading(false)
        if (session?.access_token) {
          await loadUserProfile(session.access_token);
        }

        // 3. Solo después de cargar el perfil, marcar loading como false
        setLoading(false);
      }
    }).catch(error => {
        console.error("[AuthContext] Error fetching initial session:", error);
        if (isMounted) setLoading(false);
    });

    // 2. Escuchar cambios de autenticación
    // Supabase maneja automáticamente el refresh con autoRefreshToken: true
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (isMounted) {
          console.log('[AuthContext] Auth event:', event, {
            user: session?.user ? {
              id: session.user.id?.substring(0, 6) + '...',
              email: session.user.email?.substring(0, 3) + '***@' + session.user.email?.split('@')[1]
            } : null,
            expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
            expiresIn: session?.expires_at
              ? `${Math.round((session.expires_at * 1000 - Date.now()) / 1000 / 60)} min`
              : null
          });

          setSession(session);
          setUser(session?.user ?? null);

          // 3. Cargar perfil de PostgreSQL cuando cambia la sesión.
          // Returned value used below for terms_accepted_at check (state is stale within this callback).
          let currentProfile = null;
          if (session?.access_token) {
            currentProfile = await loadUserProfile(session.access_token);
          } else {
            // Si no hay sesión (logout), limpiar el perfil
            setUserProfile(null);
          }

          // Aplicar código de registro pendiente tras confirmación de email.
          //
          // Bug B + C fix (PRP invite-model-3-paths-canonical, post-F6 hotfix):
          //   - DO NOT attempt redemption if user has not accepted T&C yet.
          //     `/api/access/redeem` itself doesn't require terms, but other
          //     endpoints fired concurrently from page render (/api/billing/subscription,
          //     /api/me/org, /api/rag-jobs/active) return 451 TERMS_NOT_ACCEPTED.
          //     apiClient.js handles 451 by calling `window.location.href = '/onboarding/terms'`,
          //     which interrupts in-flight fetches — our redeem call gets cancelled
          //     mid-request and the code is lost.
          //   - Leave the code in localStorage so TermsConsentPage can redeem it
          //     after the user accepts terms (single, race-free moment).
          //   - localStorage.removeItem now happens AFTER successful redemption,
          //     so a transient failure (network blip, EMAIL_MISMATCH on retry) does
          //     not lose the pending code.
          if (event === 'SIGNED_IN' && session?.access_token) {
            // Bug A fallback: read `?flock_invite=` from URL if localStorage is empty
            // (covers cross-browser/incognito case where email confirmation opens in
            // a context without the original registration's localStorage).
            let raw = localStorage.getItem('flock_pending_reg_code');
            if (!raw) {
              const urlInvite = new URLSearchParams(window.location.search).get('flock_invite');
              if (urlInvite) {
                raw = JSON.stringify({ code: urlInvite, issuedAt: Date.now() });
                localStorage.setItem('flock_pending_reg_code', raw);
                traceRouting('AuthContext', 'PENDING_CODE_RECOVERED', { source: 'url_flock_invite' });
              }
            }
            traceRouting('AuthContext', 'SIGNED_IN', {
              hasPendingCode: !!raw,
              terms: currentProfile?.terms_accepted_at ? 'set' : 'null',
              has_logged_in: currentProfile?.has_logged_in,
            });
            console.log(`[AuthContext] SIGNED_IN: flock_pending_reg_code in localStorage = ${raw ? '✓ present' : '✗ EMPTY'}`);
            if (raw) {
              if (!currentProfile?.terms_accepted_at) {
                console.log('[AuthContext] Postponing code redemption: terms not yet accepted. TermsConsentPage will redeem.');
              } else {
                // Terms already accepted (re-login flow) → safe to redeem now.
                try {
                  const { code, issuedAt } = JSON.parse(raw);
                  const isValid = Date.now() - issuedAt < 24 * 60 * 60 * 1000;
                  console.log(`[AuthContext] parsed pending code: code=${code?.slice(0,8)}... isValid=${isValid} (age_ms=${Date.now() - issuedAt})`);
                  if (!isValid || !code) {
                    // Stale/malformed entry — discard it.
                    localStorage.removeItem('flock_pending_reg_code');
                  } else {
                    const redeemResult = await postgresBackendService.useRegistrationCode(code);
                    console.log(`[AuthContext] ✅ Código de registro pendiente aplicado (codeType=${redeemResult?.codeType || 'n/a'})`);
                    // Bug C: only clear localStorage AFTER success.
                    localStorage.removeItem('flock_pending_reg_code');
                    await loadUserProfile(session.access_token);
                    // C-11: routing por codeType. Waitlist user debe pagar Stripe en /billing/required.
                    // Para comp/comp_byok/org_invite, OnboardingGuard hace el routing normal.
                    if (redeemResult?.codeType === 'waitlist') {
                      window.location.href = '/billing/required';
                    }
                  }
                } catch (err) {
                  if (err?.code === 'EMAIL_MISMATCH' || err?.message?.includes('EMAIL_MISMATCH') || err?.message?.includes('reserved for a different email')) {
                    // Wrong email for this code — won't ever succeed for this user. Discard.
                    setInviteError('Este código de invitación está reservado para otra dirección de correo. Contacta al administrador para que te envíe una nueva invitación.');
                    localStorage.removeItem('flock_pending_reg_code');
                  } else {
                    // Transient failure: keep code in localStorage so user can retry.
                    console.error('[AuthContext] Error al aplicar código de registro pendiente (preservado en localStorage para retry):', err);
                  }
                }
              }
            }
          }

          if (event === 'TOKEN_REFRESHED') {
            console.log('[AuthContext] ✅ Token auto-refreshed by Supabase');
          }
        }
      }
    );

    // Función de limpieza
    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Función para refrescar el perfil manualmente
  const refreshUserProfile = React.useCallback(() => {
    if (session?.access_token) {
      return loadUserProfile(session.access_token);
    }
    return Promise.resolve();
  }, [session?.access_token]); // Solo depende del token

  const clearInviteError = React.useCallback(() => setInviteError(null), []);

  // Memoizar el valor del contexto
  const value = React.useMemo(() => ({
    session,
    user,
    userProfile, // Perfil de PostgreSQL con email, associated_person, etc.
    loading,
    profileLoading,
    refreshUserProfile, // Función para refrescar el perfil manualmente
    inviteError,        // Error de email-mismatch en Camino A (post-confirmation)
    clearInviteError,
  }), [session, user, userProfile, loading, profileLoading, refreshUserProfile, inviteError, clearInviteError]);

  // No renderizar children hasta que la sesión inicial se haya cargado
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Hook para consumir el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined || context === null) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}; 